import React, { useEffect, useState } from "react";
import { Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/lib/api";

const BADGES = {
  sent: { label: { fr: "Transmis au CRM", en: "Delivered to CRM", es: "Transmitido al CRM" }, className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  failed: { label: { fr: "À retransmettre", en: "To resend", es: "Para reenviar" }, className: "bg-amber-50 text-amber-900 border-amber-200" },
  not_configured: { label: { fr: "Non configuré", en: "Not configured", es: "No configurado" }, className: "bg-muted text-muted-foreground border-border" },
  pending: { label: { fr: "En cours", en: "In progress", es: "En curso" }, className: "bg-sky-50 text-sky-900 border-sky-200" },
};

const normalize = (status) => {
  if (!status) return "not_configured"; // legacy message, never pushed to the CRM
  return status === "skipped" ? "not_configured" : status;
};

const isUnread = (m) => (m.read_status || "unread") === "unread";

const READ_LABEL = {
  unread: { fr: "Nouveau", en: "New", es: "Nuevo" },
  read: { fr: "Lu", en: "Read", es: "Leído" },
};

const READ_BY = {
  fr: { read: "Lu par", unread: "Pas encore lu", at: "à" },
  en: { read: "Read by", unread: "Not read yet", at: "at" },
  es: { read: "Leído por", unread: "Aún no leído", at: "a las" },
};

const LOCALES = { fr: "fr-CA", en: "en-CA", es: "es-ES" };

export const ReadByLine = ({ message, lang }) => {
  const copy = READ_BY[lang] || READ_BY.fr;
  if (isUnread(message)) {
    return <p data-testid="admin-contact-readby">{copy.unread}</p>;
  }
  const locale = LOCALES[lang] || LOCALES.fr;
  const who = message.read_by_name || (message.read_by ? message.read_by.slice(0, 8) : "—");
  const when = message.read_at ? new Date(message.read_at) : null;
  const day = when ? when.toLocaleDateString(locale, { day: "numeric", month: "short" }) : "";
  const hour = when ? when.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" }) : "";
  return (
    <p data-testid="admin-contact-readby">
      {copy.read} {who}{when ? `, ${day} ${copy.at} ${hour}` : ""}
    </p>
  );
};

export const ReadBadge = ({ message, lang, testId }) => {
  const unread = isUnread(message);
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center border px-2 py-0.5 text-[11px] uppercase tracking-wider ${
        unread ? "border-accent bg-accent text-accent-foreground" : "border-transparent text-muted-foreground"
      }`}
    >
      {(unread ? READ_LABEL.unread : READ_LABEL.read)[lang] || (unread ? READ_LABEL.unread : READ_LABEL.read).fr}
    </span>
  );
};

export const CrmBadge = ({ status, lang, testId }) => {
  const key = normalize(status);
  const badge = BADGES[key] || BADGES.pending;
  return (
    <span data-testid={testId} className={`inline-flex items-center border px-2 py-0.5 text-[11px] uppercase tracking-wider ${badge.className}`}>
      {badge.label[lang] || badge.label.fr}
    </span>
  );
};

const CHANNELS = {
  consultantsparks_direct: { fr: "Direct", en: "Direct", es: "Directo" },
  consultantsparks_internal: { fr: "Navigation interne", en: "Internal navigation", es: "Navegación interna" },
  organic: { fr: "Organique", en: "Organic", es: "Orgánico" },
  referral: { fr: "Référence", en: "Referral", es: "Referencia" },
  social: { fr: "Réseaux sociaux", en: "Social", es: "Redes sociales" },
  campaign: { fr: "Campagne", en: "Campaign", es: "Campaña" },
};

const LANGUAGES = {
  fr: { fr: "Français", en: "French", es: "Francés" },
  en: { fr: "Anglais", en: "English", es: "Inglés" },
  es: { fr: "Espagnol", en: "Spanish", es: "Español" },
};

const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const pageLabel = (path) => {
  const segments = (path || "").split("/").filter(Boolean);
  const last = segments[segments.length - 1];
  return last ? titleCase(last.replace(/-/g, " ")) : null;
};

// Compact list hint: campaign source if any, otherwise the page, plus the language.
const sourceHint = (message) => {
  const source = message.source;
  if (!source) return null;
  const main = source.utm_source
    ? titleCase(source.utm_source)
    : pageLabel(source.source_page || source.landing_page);
  const lang = (source.language || message.preferred_language || "").toUpperCase();
  return [main, lang].filter(Boolean).join(" · ") || null;
};

export const SourceBlock = ({ message, lang, t }) => {
  const source = message.source;
  const rows = [];
  if (source) {
    if (source.source_page) rows.push([t("contactAdmin.srcPage"), source.source_page]);
    if (source.landing_page && source.landing_page !== source.source_page) {
      rows.push([t("contactAdmin.srcLanding"), source.landing_page]);
    }
    if (source.language) rows.push([t("contactAdmin.srcLanguage"), (LANGUAGES[source.language] || {})[lang] || source.language]);
    if (source.channel) rows.push([t("contactAdmin.srcChannel"), (CHANNELS[source.channel] || {})[lang] || source.channel]);
    if (source.referrer) rows.push([t("contactAdmin.srcReferrer"), source.referrer]);
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((key) => {
      if (source[key]) rows.push([`UTM ${key.replace("utm_", "")}`, source[key]]);
    });
  }
  return (
    <div data-testid="admin-contact-source" className="border-t border-border pt-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("contactAdmin.source")}</p>
      {rows.length === 0 ? (
        <p data-testid="admin-contact-source-unknown" className="mt-1 text-xs text-muted-foreground">
          {t("contactAdmin.sourceUnknown")}
        </p>
      ) : (
        <dl className="mt-2 space-y-1 text-xs">
          {rows.map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <dt className="min-w-[110px] text-muted-foreground">{label}</dt>
              <dd className="break-all">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

export default function AdminContact() {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crm, setCrm] = useState({ configured: false });
  const [resending, setResending] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/contact-messages").then((res) => setMessages(res.data));

  useEffect(() => {
    Promise.all([load(), api.get("/admin/contact-crm-status").then((res) => setCrm(res.data))])
      .finally(() => setLoading(false));
  }, []);

  const resend = async (id) => {
    setResending(id);
    try {
      const res = await api.post(`/admin/contact-messages/${id}/resend`);
      await load();
      toast.success(res.data.crm_sync?.status === "sent"
        ? t("contactAdmin.resendOk")
        : t("contactAdmin.resendFailed"));
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    } finally {
      setResending(null);
    }
  };

  const detail = messages.find((m) => m.id === selected);
  const failedCount = messages.filter((m) => normalize(m.crm_sync?.status) === "failed").length;
  const unreadCount = messages.filter(isUnread).length;
  const visible = messages.filter((m) => {
    if (filter === "failed") return normalize(m.crm_sync?.status) === "failed";
    if (filter === "unread") return isUnread(m);
    return true;
  });

  const open = async (id) => {
    setSelected(id);
    const message = messages.find((m) => m.id === id);
    if (!message || !isUnread(message)) return;
    await api.post(`/admin/contact-messages/${id}/read`);
    load();
  };

  const FILTERS = [
    ["all", `${t("contactAdmin.filterAll")} (${messages.length})`],
    ["unread", `${t("contactAdmin.filterUnread")} (${unreadCount})`],
    ["failed", `${t("contactAdmin.filterFailed")} (${failedCount})`],
  ];

  return (
    <div data-testid="admin-contact" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-light">{t("contactAdmin.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("contactAdmin.lead")}</p>
        {!crm.configured && (
          <p data-testid="admin-contact-crm-warning" className="mt-3 border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
            {t("contactAdmin.notConfigured")}
          </p>
        )}
      </div>

      {!loading && messages.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`admin-contact-filter-${key}`}
              onClick={() => setFilter(key)}
              className={`border px-4 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                filter === key ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">…</p>}

      {!loading && messages.length === 0 && (
        <p data-testid="admin-contact-empty" className="border border-border bg-surface p-8 text-sm text-muted-foreground">
          {t("contactAdmin.empty")}
        </p>
      )}

      {messages.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <ul data-testid="admin-contact-list" className="divide-y divide-border border border-border bg-surface lg:col-span-5">
            {visible.length === 0 && (
              <li data-testid="admin-contact-filter-empty" className="px-4 py-6 text-sm text-muted-foreground">
                {filter === "unread" ? t("contactAdmin.noUnread") : t("contactAdmin.noFailed")}
              </li>
            )}
            {visible.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  data-testid={`admin-contact-row-${m.id}`}
                  onClick={() => open(m.id)}
                  className={`flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted ${selected === m.id ? "bg-secondary" : ""}`}
                >
                  <span className="flex items-center gap-2 text-sm">
                    <Mail className="h-3.5 w-3.5 text-accent" />
                    <span className={isUnread(m) ? "font-semibold" : ""}>{m.name}</span>
                    <span className="ml-auto flex items-center gap-2">
                      <ReadBadge message={m} lang={lang} testId={`admin-contact-read-${m.id}`} />
                      <CrmBadge status={m.crm_sync?.status} lang={lang} testId={`admin-contact-badge-${m.id}`} />
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(m.created_at).toLocaleString()} · {m.subject}
                  </span>
                  {sourceHint(m) && (
                    <span data-testid={`admin-contact-source-hint-${m.id}`} className="text-[11px] uppercase tracking-wider text-accent">
                      {sourceHint(m)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="lg:col-span-7">
            {!detail && (
              <p className="border border-border bg-surface p-8 text-sm text-muted-foreground">
                {t("contactAdmin.select")}
              </p>
            )}
            {detail && (
              <div data-testid="admin-contact-detail" className="space-y-4 border border-border bg-surface p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-lg">{detail.subject}</h2>
                  <ReadBadge message={detail} lang={lang} testId="admin-contact-detail-read" />
                  <CrmBadge status={detail.crm_sync?.status} lang={lang} testId="admin-contact-detail-badge" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {detail.name} · {detail.email} · {detail.preferred_language?.toUpperCase()}
                </p>
                <p data-testid="admin-contact-detail-message" className="whitespace-pre-wrap text-sm">{detail.message}</p>
                <SourceBlock message={detail} lang={lang} t={t} />
                <div className="border-t border-border pt-4 text-xs text-muted-foreground">
                  <ReadByLine message={detail} lang={lang} />
                  <p className="mt-1">{t("contactAdmin.syncLabel")} : {normalize(detail.crm_sync?.status)}
                    {detail.crm_sync?.http_status ? ` · HTTP ${detail.crm_sync.http_status}` : ""}
                    {detail.crm_sync?.attempts ? ` · ${detail.crm_sync.attempts} ${t("contactAdmin.attempts")}` : ""}
                  </p>
                  {detail.crm_sync?.updated_at && <p className="mt-1">{new Date(detail.crm_sync.updated_at).toLocaleString()}</p>}
                  {detail.crm_sync?.reason && <p className="mt-1">{detail.crm_sync.reason}</p>}
                </div>
                {normalize(detail.crm_sync?.status) === "failed" && crm.configured && (
                  <button
                    type="button"
                    data-testid="admin-contact-resend"
                    disabled={resending === detail.id}
                    onClick={() => resend(detail.id)}
                    className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2 text-sm text-accent-foreground transition-colors hover:bg-accent/90 disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${resending === detail.id ? "animate-spin" : ""}`} />
                    {t("contactAdmin.resend")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
